import { AfterViewInit, DestroyRef, Directive, ElementRef, inject, signal } from '@angular/core';
import {
  canMeasureFillHeightHost,
  computeFallbackMaxFillHeightPx,
  computeMaxFillHeightPx,
  FILL_HEIGHT_TAB_PANEL_HEIGHT_ATTR,
  isNestedFillUnderNonFillRoot,
  measureRemainingHeightAboveFooterPx,
  observeFillHeightResize,
  prefersCssFlexFillHeight,
} from './formly-tab-fill-height.layout';

/**
 * Formly list field host: fill-height layout while the active tab has `props.fillHeight`
 * (formly-tabs gets `.formly-tabs--fill-height`).
 *
 * - **Document edit** ({@link prefersCssFlexFillHeight}): CSS flex only — no px measure / ResizeObserver.
 * - **Elsewhere** (split pane, etc.): px height via `--phx-measured-fill-*` + trimmed resize observes.
 *
 * Uses `[class.…]` toggles (not `[class]="…"`) so other host class bindings
 * (e.g. `phx-base-list--content-height`) are not wiped when fill layout is inactive.
 */
@Directive({
  selector: '[formlyFillHeightListHost]',
  standalone: true,
  host: {
    '[class.phx-fill-height-measured-host]': 'fillLayoutActive()',
    '[class.flex]': 'fillLayoutActive()',
    '[class.min-h-0]': 'fillLayoutActive()',
    '[class.flex-1]': 'fillLayoutActive()',
    '[class.flex-col]': 'fillLayoutActive()',
    '[class.w-full]': 'fillLayoutActive()',
    '[class.overflow-hidden]': 'fillLayoutActive()',
    // CSS var + SCSS `height: var(...) !important` so fill-height-flex-chain cannot win (measure path).
    '[style.--phx-measured-fill-height]': 'heightPx() !== null ? heightPx() + "px" : null',
    '[style.--phx-measured-fill-max-height]': 'maxHeightPx() !== null ? maxHeightPx() + "px" : null',
    '[style.overflow]': 'heightPx() !== null || maxHeightPx() !== null ? "hidden" : null',
  },
})
export class FormlyFillHeightListHostDirective implements AfterViewInit {

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  readonly fillLayoutActive = signal(false);

  readonly heightPx = signal<number | null>(null);
  readonly maxHeightPx = signal<number | null>(null);

  /** Synchronous cap before paint when a fill-height tab becomes active (prevents overshoot flicker). */
  applyTabActivationCap(): void {
    this.applyInterimCap(this.elementRef.nativeElement.closest('p-tabpanel'));
  }

  ngAfterViewInit(): void {
    const host = this.elementRef.nativeElement;
    const tabPanel = host.closest('p-tabpanel');

    const updateHeight = (): void => {
      const formlyTabs = host.closest('formly-tabs');
      const panel = host.closest('p-tabpanel');
      const isFill = !!formlyTabs?.classList.contains('formly-tabs--fill-height');
      // Nested fill under non-fill root (product Verkauf sub-tabs): content-size, no clip.
      const useMeasuredFill = isFill && !isNestedFillUnderNonFillRoot(host);
      this.fillLayoutActive.set(useMeasuredFill);

      if (!useMeasuredFill) {
        this.heightPx.set(null);
        this.maxHeightPx.set(null);
        return;
      }

      // Root document fill tabs: shell flex owns height — marker class only.
      // Re-check each update (nested vs root can differ once fill-height class lands).
      if (prefersCssFlexFillHeight(host)) {
        this.heightPx.set(null);
        this.maxHeightPx.set(null);
        return;
      }

      if (panel && !panel.classList.contains('p-tabpanel-active')) {
        // Keep last measured px on hidden panels — clearing causes a tall-frame flicker on re-activate.
        return;
      }

      this.applyInterimCap(panel);

      const maxAllowed = this.maxHeightPx();
      if (!canMeasureFillHeightHost(host) || maxAllowed == null) {
        return;
      }

      const measured = measureRemainingHeightAboveFooterPx(host);
      const next = Math.max(Math.min(measured, maxAllowed), 0);
      if (this.heightPx() !== next) {
        this.heightPx.set(next);
      }
      // Keep max in sync with applied height — avoids interim overshoot then shrink (footer race).
      if (this.maxHeightPx() !== next) {
        this.maxHeightPx.set(next);
      }
      panel?.setAttribute(FILL_HEIGHT_TAB_PANEL_HEIGHT_ATTR, String(next));
    };

    const scheduleUpdate = (): void => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(updateHeight);
        });
      });
    };

    this.applyInterimCap(tabPanel);
    scheduleUpdate();

    // Always observe — nested document fill tabs still measure; root CSS-flex path
    // no-ops quickly in updateHeight when prefersCssFlexFillHeight is true.
    const teardowns: Array<() => void> = [observeFillHeightResize(host, scheduleUpdate)];

    if (tabPanel) {
      const panelObserver = new MutationObserver(() => {
        if (!tabPanel.classList.contains('p-tabpanel-active')) {
          return;
        }
        this.applyInterimCap(tabPanel);
        scheduleUpdate();
      });
      panelObserver.observe(tabPanel, { attributes: true, attributeFilter: ['class'] });
      teardowns.push(() => panelObserver.disconnect());
    }

    const formlyTabs = host.closest('formly-tabs');
    if (formlyTabs) {
      const tabsObserver = new MutationObserver(() => {
        if (
          formlyTabs.classList.contains('formly-tabs--fill-height')
          && !prefersCssFlexFillHeight(host)
        ) {
          this.applyInterimCap(tabPanel);
        }
        scheduleUpdate();
      });
      tabsObserver.observe(formlyTabs, { attributes: true, attributeFilter: ['class'] });
      teardowns.push(() => tabsObserver.disconnect());
    }

    this.destroyRef.onDestroy(() => teardowns.forEach((teardown) => teardown()));
  }

  private applyInterimCap(tabPanel: Element | null): void {
    const host = this.elementRef.nativeElement;
    const formlyTabs = host.closest('formly-tabs');
    if (
      !formlyTabs?.classList.contains('formly-tabs--fill-height')
      || isNestedFillUnderNonFillRoot(host)
    ) {
      this.fillLayoutActive.set(false);
      this.heightPx.set(null);
      this.maxHeightPx.set(null);
      return;
    }
    this.fillLayoutActive.set(true);

    // CSS flex path: never seed measured px (would fight flex height:0).
    if (prefersCssFlexFillHeight(host)) {
      this.heightPx.set(null);
      this.maxHeightPx.set(null);
      return;
    }

    const cachedRaw = tabPanel?.getAttribute(FILL_HEIGHT_TAB_PANEL_HEIGHT_ATTR);
    const cached = cachedRaw
      ? (() => {
        const parsed = Number.parseInt(cachedRaw, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      })()
      : null;

    const maxAllowed = computeMaxFillHeightPx(host)
      ?? computeFallbackMaxFillHeightPx(host)
      ?? cached;
    if (maxAllowed != null) {
      this.maxHeightPx.set(maxAllowed);
    }
    if (cached != null) {
      this.heightPx.set(cached);
    }
  }
}
